import type * as Y from 'yjs';

/**
 * @internal Please don't use this. This is the value that Yjs defaults when a transaction doesn't specify an origin
 */
export const YJS_DEFAULT = null;

/**
 * A subset of a {@link Y.Transaction} containing only basic properties needed.
 */
export type Transaction = Pick<Y.Transaction, 'origin' | 'local'>;

/**
 * {@link Transaction} except with types that are more specific to Canvas
 */
export type CanvasTransaction = {
  origin: TransactionOrigin;
} & Omit<Transaction, 'origin'>;

/**
 * The values we currently allow to be added to collab transactions when our own app logic mutates the collab state
 *
 * Used in the Transaction's origin field and affects things like what is tracked in the undo manager
 * @see https://docs.yjs.dev/api/y.doc
 */
export enum CanvasTransactionOrigin {
  /**
   * Specifies whether the transaction is making a mutation for transitory state. This includes things like progress status values.
   *
   * Populating the origin with this value will avoid recording any information about the transaction in our undo manager
   */
  LocalTransitorySystem = 'transitory-system',
  /**
   * At the moment this is really just to differentiate mutations that are transitory from those that are not.
   */
  RemoteUndoManager = 'undo-manager',
  LocalDefault = 'local-default',
  /**
   * A new origin for timer because we don't want the timer changes to be un-doable
   */
  Timer = 'timer',
  /**
   * A new origin for elements which live in external systems
   */
  ExternallyOwnedElement = 'externally-owned',
  /**
   * Transactions that come from the document being cleaned.
   * This involves scrubbing untrusted data that can be set by an
   * anonymous/external user.
   */
  CleanDocument = 'clean-document',
  /**
   * Transactions that come from the private session manager.
   */
  PrivateSessionManager = 'private-session-manager',
  /**
   * Transactions that come from an AI agent.
   */
  Agent = 'ai-agent',
  /**
   * Used to denote transactions that come from an AI agent
   * but should be processed without any interpolation
   */
  AgentImmediate = 'ai-agent-immediate',
  /**
   * No specific origin but can be used to ensure that we trigger the same
   * document observers as a remote transaction would
   */
  TriggerObservers = 'trigger-observers',
  /**
   * Transactions that come from a remote user resizing
   */
  Resizing = 'resizing',
  /**
   * Layout-driven position shifts.
   * These should be applied immediately without interpolation on all clients.
   */
  LayoutShift = 'layout-shift',
  /**
   * User-initiated ungrouping.
   *
   * Ungrouping deletes the group element and clears its membership, but the
   * group's parent ECS entity is torn down by document observers rather than by
   * the ungrouping system directly. That means the transaction must be
   * processed by observers (see `needsProcessingByObservers`) AND it must be
   * tracked by the undo manager so the ungroup can be undone. `LocalDefault` is
   * tracked but skipped by observers, and `TriggerObservers` is processed by
   * observers but not tracked, so neither works on its own.
   */
  Ungrouping = 'ungrouping',
}

/**
 * Origins that must be applied immediately without interpolation.
 * Consumed by the interpolation layer and the update-merge logic to
 * ensure these origins are never silently dropped or smoothed over.
 */
export const IMMEDIATE_ORIGINS: ReadonlySet<CanvasTransactionOrigin> = new Set([
  CanvasTransactionOrigin.AgentImmediate,
  CanvasTransactionOrigin.LayoutShift,
]);

export type YjsOrigin = Record<never, never>;

/**
 * All possible transaction origins types that you'll see when inspecting the collab transaction.origin
 */
export type TransactionOrigin =
  | CanvasTransactionOrigin
  | typeof YJS_DEFAULT
  | Y.UndoManager
  | YjsOrigin
  | undefined;
// TODO CNS-2170: YSocket - Current a circular dependency @canvas/transaction -> @canvas/model -> @canvas/collab -> repeat
